import type { DraftEducation, DraftExperience, ResumeDraft } from "./draft";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

export type YearMonth = { year: number; month: number };

const PRESENT_RE = /^(now|present|current|today)$/i;
const YEAR_RE = /^(19|20)\d{2}$/;
const INTERN_RE = /\bintern(?:ship)?\b/i;

export const addMonths = (value: YearMonth, delta: number): YearMonth => {
  const index = value.year * 12 + value.month + delta;
  const year = Math.floor(index / 12);
  const month = ((index % 12) + 12) % 12;
  return { year, month };
};

export const yearMonthFromDate = (value: Date): YearMonth => ({
  year: value.getFullYear(),
  month: value.getMonth(),
});

export const formatMonthYear = (value: YearMonth): string =>
  `${MONTHS[value.month]} ${value.year}`;

export const formatExperienceRange = (start: YearMonth, end: YearMonth | "present"): string =>
  end === "present"
    ? `${formatMonthYear(start)} - Present`
    : `${formatMonthYear(start)} - ${formatMonthYear(end)}`;

export const rangeTenureMonths = (value: string, now: Date = new Date()): number | null => {
  const stated = parseDurationMonths(value);
  if (stated) return stated;

  const range = splitRange(value);
  if (!range) return null;
  const start = parseBound(range[0]);
  const end = parseBound(range[1]);
  if (!start || start === "present") return null;

  const startYm = boundToStart(start);
  if (!startYm) return null;

  let endYm: YearMonth | null;
  if (!end || end === "present") {
    endYm = yearMonthFromDate(now);
  } else if ("month" in end) {
    endYm = end;
  } else {
    endYm = { year: end.year, month: "month" in start ? start.month : 11 };
  }
  if (!endYm) return null;

  return Math.max(0, endYm.year * 12 + endYm.month - (startYm.year * 12 + startYm.month));
};

export const parseDurationMonths = (value: string): number | null => {
  const text = value.trim();
  if (!text) return null;
  if (/below\s*1\s*year|less than (?:a |one |1 )?year|under (?:a |1 )?year/i.test(text)) {
    return 6;
  }
  const years = text.match(/(\d+)\s*\+?\s*years?/i);
  const months = text.match(/(\d+)\s*\+?\s*months?/i);
  if (years && months) return Number(years[1]) * 12 + Number(months[1]);
  if (years) return Number(years[1]) * 12;
  if (months) return Number(months[1]);
  return null;
};

const parseMonthToken = (value: string): number | null => {
  const key = value.replace(/\./g, "").trim().toLowerCase();
  return key in MONTH_INDEX ? MONTH_INDEX[key] : null;
};

type Bound = YearMonth | "present" | { year: number };

const parseBound = (raw: string): Bound | null => {
  const text = raw.trim();
  if (!text) return null;
  if (PRESENT_RE.test(text)) return "present";

  const monthYear = text.match(
    /^(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+((?:19|20)\d{2})$/i,
  );
  if (monthYear) {
    const month = parseMonthToken(monthYear[1] ?? "");
    const year = Number(monthYear[2]);
    if (month === null || !year) return null;
    return { year, month };
  }

  const yearMonth = text.match(
    /^((?:19|20)\d{2})\s*[./-]?\s*(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?$/i,
  );
  if (yearMonth) {
    const year = Number(yearMonth[1]);
    const month = parseMonthToken(yearMonth[2] ?? "");
    if (month === null || !year) return null;
    return { year, month };
  }

  if (YEAR_RE.test(text)) return { year: Number(text) };
  return null;
};

const splitRange = (value: string): [string, string] | null => {
  const parts = value
    .trim()
    .split(/\s*(?:-|–|—|to)\s*/i)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 2) return [parts[0], parts[1]];
  return null;
};

export const looksLikeCalendarDate = (value: string): boolean => {
  const text = value.trim();
  if (!text) return false;
  if (parseDurationMonths(text) !== null && !/\b(19|20)\d{2}\b/.test(text)) return false;
  return Boolean(parseBound(text) || splitRange(text));
};

const boundToStart = (bound: Bound, fallbackMonth = 0): YearMonth | null => {
  if (bound === "present") return null;
  if ("month" in bound) return bound;
  return { year: bound.year, month: fallbackMonth };
};

export const normalizeCalendarDate = (value: string): string => {
  const text = value.trim().replace(/\s+/g, " ");
  if (!text) return "";

  const range = splitRange(text);
  if (range) {
    const start = parseBound(range[0]);
    const end = parseBound(range[1]);
    if (!start || !end) return text;
    if (start === "present") return text;

    const startHasMonth = "month" in start;
    const startYm = boundToStart(start);
    if (!startYm) return text;

    if (end === "present") {
      return startHasMonth ? formatExperienceRange(startYm, "present") : `${startYm.year} - Present`;
    }
    const endYear = "year" in end ? end.year : startYm.year;
    if (startHasMonth && "month" in end) return formatExperienceRange(startYm, end);
    return `${startYm.year} - ${endYear}`;
  }

  const bound = parseBound(text);
  if (!bound || bound === "present") return text;
  if ("month" in bound) return formatMonthYear(bound);
  return String(bound.year);
};

const parseStartYearMonth = (value: string): YearMonth | null => {
  const text = value.trim();
  const range = splitRange(text);
  const startToken = range ? range[0] : text;
  const bound = parseBound(startToken);
  if (!bound || bound === "present") return null;
  return boundToStart(bound);
};

const inferredDuration = (item: DraftExperience, index: number): number | null => {
  const stated = parseDurationMonths(item.date ?? "");
  if (stated) return stated;
  if ((item.date ?? "").trim()) return null;
  if (index > 0 && INTERN_RE.test(item.position ?? "")) return 3;
  return null;
};

const applyExperienceDates = (items: DraftExperience[], now: YearMonth): DraftExperience[] => {
  let cursor = now;

  return items.map((item, index) => {
    const raw = item.date?.trim() ?? "";
    if (raw && looksLikeCalendarDate(raw)) {
      const next = { ...item, date: normalizeCalendarDate(raw) };
      const start = parseStartYearMonth(next.date ?? "");
      if (start) cursor = start;
      return next;
    }

    const months = inferredDuration(item, index);
    if (!months) return { ...item, date: raw };

    const isCurrent = index === 0;
    const end: YearMonth | "present" = isCurrent ? "present" : cursor;
    const start = addMonths(isCurrent ? now : cursor, -months);
    cursor = start;
    return { ...item, date: formatExperienceRange(start, end) };
  });
};

const applyEducationDates = (
  items: DraftEducation[],
  firstJobStart: YearMonth | null,
  now: YearMonth,
): DraftEducation[] => {
  return items.map((item) => {
    const raw = item.date?.trim() ?? "";
    if (raw && looksLikeCalendarDate(raw)) {
      return { ...item, date: normalizeCalendarDate(raw) };
    }

    const months = parseDurationMonths(raw);
    if (!months) return item;

    const end = firstJobStart ?? now;
    const start = addMonths(end, -months);
    if (!firstJobStart) {
      return { ...item, date: `${start.year} - Present` };
    }
    return { ...item, date: `${start.year} - ${end.year}` };
  });
};

export const applyCalendarDates = (draft: ResumeDraft, now: Date = new Date()): ResumeDraft => {
  const nowYm = yearMonthFromDate(now);
  const experience = applyExperienceDates([...(draft.experience ?? [])], nowYm);
  const firstStart = experience[0]?.date ? parseStartYearMonth(experience[0].date) : null;
  const education = applyEducationDates([...(draft.education ?? [])], firstStart, nowYm);

  const basics = { ...draft.basics };
  if (!basics.location?.trim() && experience[0]?.location?.trim()) {
    basics.location = experience[0].location.trim();
  }
  if (!basics.location?.trim()) {
    basics.location = "Remote";
  }

  return { ...draft, basics, experience, education };
};
